import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

export const sendChat = (payload) => apiClient.post("/chat/message", payload).then((r) => r.data);
export const submitLead = (payload) => apiClient.post("/leads", payload).then((r) => r.data);
export const fetchRegion = () => apiClient.get("/region").then((r) => r.data);
export const fetchTopFaq = (lang = "tr") => apiClient.get(`/faq/top?language=${lang}`).then((r) => r.data);
export const adminAgentChat = (payload) => apiClient.post("/admin/agent/chat", payload, { timeout: 60000 }).then((r) => r.data);

// Silent telemetry — used to report client-side issues (TTS, audio, mic) to admin
export const reportClientError = (payload) => {
  // fire-and-forget; never blocks UI
  try {
    apiClient.post("/telemetry/client-error", {
      kind: payload.kind, message: payload.message || "",
      user_agent: navigator.userAgent || "", url: window.location.href || "",
      language: payload.language || "", session_id: payload.session_id || "",
    }).catch(() => {});
  } catch {/* ignore */}
};

export const ttsSpeak = async (text) => {
  const r = await fetch(`${API}/tts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!r.ok) throw new Error("tts-unavailable");
  return await r.blob();
};

export const transcribeAudio = async (blob, language) => {
  const fd = new FormData();
  fd.append("audio", blob, "voice.webm");
  fd.append("language", language || "tr");
  const r = await fetch(`${API}/chat/voice`, { method: "POST", body: fd });
  if (!r.ok) throw new Error("stt-failed");
  return await r.json();
};

// Admin
export const listKnowledge = () => apiClient.get("/knowledge").then((r) => r.data);
export const addManualKnowledge = (p) => apiClient.post("/knowledge/manual", p).then((r) => r.data);
export const deleteKnowledge = (id) => apiClient.delete(`/knowledge/${id}`).then((r) => r.data);
export const crawlSite = (p) => apiClient.post("/knowledge/crawl", p, { timeout: 300000 }).then((r) => r.data);

export const listPricing = () => apiClient.get("/pricing").then((r) => r.data);
export const upsertPricing = (p) => apiClient.post("/pricing", p).then((r) => r.data);
export const deletePricing = (id) => apiClient.delete(`/pricing/${id}`).then((r) => r.data);
export const getExchangeRate = () => apiClient.get("/pricing/exchange-rate").then((r) => r.data);
export const uploadPricingExcel = async (file, replace = false) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("replace", replace ? "true" : "false");
  const r = await apiClient.post("/pricing/upload-excel", fd, {
    headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
  });
  return r.data;
};

export const getWorkingHours = () => apiClient.get("/working-hours").then((r) => r.data);
export const updateWorkingHours = (p) => apiClient.put("/working-hours", p).then((r) => r.data);

export const listCampaigns = () => apiClient.get("/campaigns").then((r) => r.data);
export const upsertCampaign = (p) => apiClient.post("/campaigns", p).then((r) => r.data);
export const deleteCampaign = (id) => apiClient.delete(`/campaigns/${id}`).then((r) => r.data);
export const uploadCampaignsExcel = async (file, replace = false) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("replace", replace ? "true" : "false");
  const r = await apiClient.post("/campaigns/upload-excel", fd, {
    headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
  });
  return r.data;
};
export const listCampaignLeads = () => apiClient.get("/campaign-leads").then((r) => r.data);
export const deleteCampaignLead = (id) => apiClient.delete(`/campaign-leads/${id}`).then((r) => r.data);
export const downloadCampaignLeadsXlsx = () => `${API}/campaign-leads/export.xlsx`;

export const listLeads = () => apiClient.get("/leads").then((r) => r.data);
export const deleteLead = (id) => apiClient.delete(`/leads/${id}`).then((r) => r.data);

export const listConversations = () => apiClient.get("/conversations").then((r) => r.data);
export const getConversation = (sid) => apiClient.get(`/conversations/${sid}`).then((r) => r.data);

export const trackVisit = () => apiClient.post("/track/visit").then((r) => r.data).catch(() => null);
export const getAnalytics = () => apiClient.get("/analytics").then((r) => r.data);

export const getInstructions = () => apiClient.get("/instructions").then((r) => r.data);
export const updateInstructions = (p) => apiClient.put("/instructions", p).then((r) => r.data);

export const getSettings = () => apiClient.get("/settings").then((r) => r.data);
export const updateSettings = (p) => apiClient.put("/settings", p).then((r) => r.data);

export const declineLead = (p) => apiClient.post("/leads/decline", p).then((r) => r.data);
export const getDigest = (date) => apiClient.get("/digest", { params: date ? { date } : {} }).then((r) => r.data);
export const getDigestInsights = (date) => apiClient.get("/digest/insights", { params: date ? { date } : {}, timeout: 60000 }).then((r) => r.data);
export const sendDigestNow = (date) => apiClient.post("/digest/send-now", null, { params: date ? { date } : {}, timeout: 90000 }).then((r) => r.data);
export const getDigestLogs = () => apiClient.get("/digest/logs").then((r) => r.data);
export const listDeclineReasons = () => apiClient.get("/decline-reasons").then((r) => r.data);

export const listRecipients = () => apiClient.get("/recipients").then((r) => r.data);
export const upsertRecipient = (p) => apiClient.post("/recipients", p).then((r) => r.data);
export const deleteRecipient = (id) => apiClient.delete(`/recipients/${id}`).then((r) => r.data);

// Security: password change + magic link management
export const changeAdminPassword = (new_password) =>
  apiClient.post("/admin/change-password", { new_password }).then((r) => r.data);
export const getAdminMagicLink = () => apiClient.get("/admin/magic-link").then((r) => r.data);
export const rotateAdminMagicLink = () => apiClient.post("/admin/magic-link/rotate").then((r) => r.data);
